/**
 * The letter body: validation and rendering.
 *
 * A letter is **built, not written** (Q10.1). It is an opening, one to three
 * slot-filled statements and a closing, all of them ids into
 * `content/<locale>/letter-grammar.json`. Two players who chose the same
 * template and the same slots produce byte-identical prose, which is why there
 * is no writing style to fingerprint (anonymity R6) and no free text to abuse.
 *
 * A body is **stored as ids** and rendered in the *reader's* locale (Q12.2), so
 * the grammar passed to `renderLetterBody` is the reader's file, not the
 * sender's. That is how a German player reads an English player's letter.
 *
 * Validation checks that every id exists, that every slot is filled from its
 * own list, and that a coordinate is on the grid. It checks nothing else --
 * lying is a mechanic and truth is judged later.
 */

import { z } from 'zod'
import { GAME_CONFIG } from '../game-config.ts'
import { COORD_SLOT, type LetterGrammarFile, type SlotType } from '../content/schemas.ts'

const { grid, letter } = GAME_CONFIG

/** A coordinate a letter quotes, rendered as `x, y`. */
export type LetterCoord = { readonly x: number; readonly y: number }

/** A filled slot: an id from its own list, or a coordinate. */
export type LetterSlotValue = string | LetterCoord

export type LetterStatement = {
  readonly statement: string
  readonly slots: Readonly<Record<string, LetterSlotValue>>
}

/** A letter body as `letters.body_json` and `inbox_letters.body_json` hold it. */
export type LetterBody = {
  readonly opening: string
  readonly statements: readonly LetterStatement[]
  readonly closing: string
}

export type LetterBodyError =
  | 'malformed'
  | 'no-such-opening'
  | 'no-such-closing'
  | 'no-such-statement'
  | 'wrong-statement-count'
  | 'slot-unfilled'
  | 'no-such-slot'
  | 'no-such-slot-value'
  | 'coord-off-the-island'

export type LetterBodyResult =
  | { readonly ok: true; readonly body: LetterBody }
  | { readonly ok: false; readonly error: LetterBodyError }

const coordSchema = z.object({ x: z.number(), y: z.number() })

/**
 * The wire shape. Unknown keys are stripped, so a handler that attached its own
 * state to a body cannot have that reach a recipient (anonymity R5).
 */
export const letterBodySchema = z.object({
  opening: z.string(),
  statements: z.array(
    z.object({
      statement: z.string(),
      slots: z.record(z.string(), z.union([z.string(), coordSchema])),
    }),
  ),
  closing: z.string(),
})

const onTheGrid = (coord: LetterCoord): boolean =>
  Number.isInteger(coord.x) &&
  Number.isInteger(coord.y) &&
  coord.x >= 0 &&
  coord.x < grid.width &&
  coord.y >= 0 &&
  coord.y < grid.height

const isCoord = (value: LetterSlotValue): value is LetterCoord => typeof value !== 'string'

/**
 * Check a body against a grammar. The grammar may be any locale's: slot maps
 * and ids are identical across locales by contract, so validity is too.
 */
export function validateLetterBody(body: unknown, grammar: LetterGrammarFile): LetterBodyResult {
  const parsed = letterBodySchema.safeParse(body)
  if (!parsed.success) return { ok: false, error: 'malformed' }
  const { opening, statements, closing } = parsed.data

  if (!grammar.openings.some((entry) => entry.id === opening)) {
    return { ok: false, error: 'no-such-opening' }
  }
  if (!grammar.closings.some((entry) => entry.id === closing)) {
    return { ok: false, error: 'no-such-closing' }
  }
  if (
    statements.length < letter.statementsPerLetter.min ||
    statements.length > letter.statementsPerLetter.max
  ) {
    return { ok: false, error: 'wrong-statement-count' }
  }

  for (const filled of statements) {
    const statement = grammar.statements.find((entry) => entry.id === filled.statement)
    if (statement === undefined) return { ok: false, error: 'no-such-statement' }

    for (const name of Object.keys(filled.slots)) {
      if (statement.slots[name] === undefined) return { ok: false, error: 'no-such-slot' }
    }

    for (const [name, type] of Object.entries(statement.slots)) {
      const value = filled.slots[name]
      if (value === undefined) return { ok: false, error: 'slot-unfilled' }
      if (type === COORD_SLOT) {
        if (!isCoord(value) || !onTheGrid(value))
          return { ok: false, error: 'coord-off-the-island' }
        continue
      }
      if (isCoord(value)) return { ok: false, error: 'no-such-slot-value' }
      const values = grammar.slots[type as SlotType]
      if (values === undefined || !values.some((entry) => entry.id === value)) {
        return { ok: false, error: 'no-such-slot-value' }
      }
    }
  }

  return { ok: true, body: { opening, statements, closing } }
}

/**
 * Render a body to prose in the locale of the grammar given.
 *
 * Throws on an id the grammar does not have, rather than doing its best: the
 * renderer must never put a word on a recipient's screen that is not in the
 * shared vocabulary. Callers validate first.
 */
export function renderLetterBody(body: LetterBody, grammar: LetterGrammarFile): string {
  const opening = grammar.openings.find((entry) => entry.id === body.opening)
  if (opening === undefined) throw new Error(`the grammar has no opening "${body.opening}"`)
  const closing = grammar.closings.find((entry) => entry.id === body.closing)
  if (closing === undefined) throw new Error(`the grammar has no closing "${body.closing}"`)

  const sentences = body.statements.map((filled) => {
    const statement = grammar.statements.find((entry) => entry.id === filled.statement)
    if (statement === undefined) {
      throw new Error(`the grammar has no statement "${filled.statement}"`)
    }
    return statement.template.replace(
      /\{([A-Za-z0-9_]+)(?:\.([A-Za-z0-9_]+))?\}/g,
      (_all, name: string, form: string | undefined) => {
        const type = statement.slots[name]
        if (type === undefined) throw new Error(`${statement.id} has no slot {${name}}`)
        const value = filled.slots[name]
        if (value === undefined) throw new Error(`${statement.id} has {${name}} unfilled`)
        if (type === COORD_SLOT) {
          if (!isCoord(value)) throw new Error(`${statement.id}: {${name}} is not a coordinate`)
          return `${value.x}, ${value.y}`
        }
        if (isCoord(value)) throw new Error(`${statement.id}: {${name}} is not a ${type}`)
        const entry = grammar.slots[type as SlotType]?.find((candidate) => candidate.id === value)
        if (entry === undefined) throw new Error(`the grammar has no ${type} "${value}"`)
        // A locale that has not supplied the form still renders, in the plain
        // text, rather than leaving a brace on a player's screen.
        return (form === undefined ? undefined : entry.forms?.[form]) ?? entry.text
      },
    )
  })

  return `${opening.text}\n\n${sentences.join(' ')}\n\n${closing.text}`
}
